import React, { useMemo, useCallback } from "react";
import Icons from "@hs/icons";
import PrincipalEditorControl from "components/ExtraAttributes/EditorControl/PrincipalEditorControl";
import EnumEditorControl from "components/ExtraAttributes/EditorControl/EnumEditorControl";
import LookupEditorControl from "components/ExtraAttributes/EditorControl/LookupEditorControl";
import Divider from "components/Divider";
import GetDefaultSearchBuckets from "data/FilterBar";
import { LabelSizeContext } from "data/context";
import {
    FormLookupType,
    SearchOperator,
    SearchValueType,
    SearchNodeType,
    type ISearchFilter,
} from "data/types";
import { getLocalizedText, getClassNames } from "data/utils";
import { produce } from "immer";
import styles from "./Mask.module.css";

type Props = {
    /** initial search scope itemUri */
    itemUri: string,
    /** search filters */
    filters: ISearchFilter[],
    /** callback when user changes advanced search text
     * @param {string} field advanced search text field
     * @param {string} value advanced search text value for that field
     */
    onFilterChange: (field: string, value: any) => void,
    /** default expanded state */
    defaultExpanded: boolean,
    /** prefix for form names */
    prefix?: string,
};

const Filters = ({
    itemUri,
    filters,
    onFilterChange,
    defaultExpanded,
    prefix = "sm",
}: Props) => {
    const isDocument = filters.some(
        (f) => f.name === "ItemType" && f.value === "Document"
    );
    const filterConfig = useMemo(
        () =>
            GetDefaultSearchBuckets().reduce(
                (config, bucket) => {
                    config[bucket.name] = {
                        header: bucket.caption,
                        valueType: bucket.valueType,
                        enum: [
                            {
                                value: null,
                                label: getLocalizedText(
                                    "$common:filter.AllCaption"
                                ),
                            },
                            ...bucket.buckets?.map(
                                ({ value, caption: label }) => ({
                                    value,
                                    label,
                                })
                            ),
                        ],
                    };
                    return config;
                },
                {
                    ItemType: {
                        header: getLocalizedText(
                            "$common:filter.ItemType.name"
                        ),
                        valueType: SearchValueType.ItemType,
                        enum: [
                            {
                                value: null,
                                label: getLocalizedText(
                                    "$common:filter.AllCaption"
                                ),
                            },
                            {
                                value: "Document",
                                label: getLocalizedText(
                                    "$common:filter.ItemType.Document"
                                ),
                                icon: "file",
                            },
                            {
                                value: "Folder",
                                label: getLocalizedText(
                                    "$common:filter.ItemType.Folder"
                                ),
                                icon: "folder-open",
                            },
                        ],
                    },
                }
            ),
        []
    );
    const extension = useMemo(
        () =>
            filters.find((f) => f.name === "Dateityp") || {
                valueType: SearchValueType.String,
                name: "Dateityp",
                op: SearchOperator.Equals,
                value: null,
            },
        [filters]
    );
    const [attributes, changes] = useMemo(() => {
        const attributes = produce([], (draft) => {
            for (let f of filters) {
                draft.push(
                    produce(f, (filter) => {
                        filter.nodeType = SearchNodeType.LeafNode;
                    })
                );
            }
            /* istanbul ignore else */
            if (!filters.some((f) => f.name === "Dateityp")) {
                draft.push(extension);
            }
        });
        return [
            attributes,
            produce({}, (draft) => {
                for (let f of attributes) {
                    draft[f.name] = produce(f, (filter) => {
                        filter.op = SearchOperator.Equals;
                        if (filter.value?.$type === "user") {
                            filter.val = filter.value.displayName;
                        } else {
                            filter.val = filter.value;
                        }
                        delete filter.value;
                    });
                }
            }),
        ];
    }, [filters, extension]);

    const handleLookupChange = useCallback(
        (fields) =>
            fields?.map((field) => onFilterChange(field.name, field.value)),
        [onFilterChange]
    );

    return (
        <Divider
            name="showSearchFilter"
            hideHR
            className={styles.header}
            defaultExpanded={defaultExpanded}
            header={
                <>
                    {getLocalizedText("$search:mask.filter")}
                    <Icons.Library
                        name="filters"
                        fixedWidth
                        className={styles.icon}
                    />
                </>
            }
        >
            <div
                className={getClassNames("filters", styles.filter)}
                data-test={`SearchMask.Filters`}
            >
                <LabelSizeContext.Provider value={125}>
                    <EnumEditorControl
                        name={`${prefix}.ItemType`}
                        header={filterConfig.ItemType.header}
                        onChange={onFilterChange}
                        enum={filterConfig.ItemType.enum}
                        value={
                            filters.find((f) => f.name === "ItemType")?.value
                        }
                    />
                    {isDocument && (
                        <>
                            <EnumEditorControl
                                name={`${prefix}.Groesse`}
                                header={filterConfig.Groesse.header}
                                onChange={onFilterChange}
                                enum={filterConfig.Groesse.enum}
                                component="Dropdown"
                                value={
                                    filters.find((f) => f.name === "Groesse")
                                        ?.value
                                }
                            />
                            <LookupEditorControl
                                name={`${prefix}.Dateityp`}
                                lookupType={FormLookupType.sf_ftlookup}
                                header={getLocalizedText(
                                    "$common:filter.Dateityp.name"
                                )}
                                valueType={SearchValueType.String}
                                itemUri={itemUri}
                                changes={changes}
                                attributes={attributes}
                                value={extension.value}
                                isMultiple={false}
                                useContains={true}
                                useKeys={false}
                                onChange={handleLookupChange}
                            />
                        </>
                    )}
                    <EnumEditorControl
                        name={`${prefix}.Geaendert_am`}
                        header={filterConfig.Geaendert_am.header}
                        onChange={onFilterChange}
                        enum={filterConfig.Geaendert_am.enum}
                        component="Dropdown"
                        value={
                            filters.find((f) => f.name === "Geaendert_am")
                                ?.value
                        }
                    />
                    <PrincipalEditorControl
                        name={`${prefix}.Geaendert_von`}
                        isClearable
                        header={getLocalizedText(
                            "$common:filter.Geaendert_von.name"
                        )}
                        onChange={onFilterChange}
                        value={
                            filters.find((f) => f.name === "Geaendert_von")
                                ?.value
                        }
                    />
                    <EnumEditorControl
                        name={`${prefix}.Erstellt_am`}
                        header={filterConfig.Erstellt_am.header}
                        onChange={onFilterChange}
                        enum={filterConfig.Erstellt_am.enum}
                        component="Dropdown"
                        value={
                            filters.find((f) => f.name === "Erstellt_am")?.value
                        }
                    />
                    <PrincipalEditorControl
                        name={`${prefix}.Erstellt_von`}
                        isClearable
                        header={getLocalizedText(
                            "$common:filter.Erstellt_von.name"
                        )}
                        onChange={onFilterChange}
                        value={
                            filters.find((f) => f.name === "Erstellt_von")
                                ?.value
                        }
                    />
                </LabelSizeContext.Provider>
            </div>
        </Divider>
    );
};
Filters.displayName = "SearchMask.Filters";

export default Filters;
